(() => {
  if (window.__aiosCartDrawerInit) return;
  window.__aiosCartDrawerInit = true;

  const cartDrawerSelector = '#CartDrawer';
  const fallbackCartSectionId = 'aios-cart-drawer';
  const ajaxFormSelector = [
    'form[data-aios-ajax-cart-form]',
    'form.aios-collection-product__form',
    'form.aios-product-main__form',
    'form.aios-top-sale-picks__form',
    'form.aios-collection-tabs-grid__form'
  ].join(', ');
  const variantQuickAddSelector = [
    'a.aios-top-sale-picks__add[aria-label^="Choose options"]',
    'a.aios-collection-tabs-grid__add[aria-label^="Choose options"]',
    '[data-aios-quick-add][href]'
  ].join(', ');
  const toolbarSearchSelector = [
    '[data-aios-toolbar-search]',
    '.aios-mobile-toolbar__item--search a[aria-controls^="drawer-search-form"]'
  ].join(', ');

  let externalCartRefreshRunning = false;
  let cartCountRequestId = 0;
  let cartDrawerRefreshId = 0;
  let variantModal = null;
  let variantModalContent = null;
  let variantModalTrigger = null;
  let productRequestController = null;

  const getCartDrawer = () => document.querySelector(cartDrawerSelector);
  const getRootUrl = () => window.Shopify?.routes?.root || '/';
  const getCartAddUrl = () => window.Shopify?.routes?.cart_add_url || `${getRootUrl()}cart/add.js`;
  const getCartChangeUrl = () => window.Shopify?.routes?.cart_change_url || `${getRootUrl()}cart/change`;
  const getCartSectionId = () => {
    const host = getCartDrawer()?.closest('hdt-cart-drawer');
    return host?.getAttribute('section-id') || fallbackCartSectionId;
  };

  const getDrawerContent = (root) => root?.querySelector('[id^="AIOSCartDrawerContent-"], .aios-cart');
  const stopEvent = (event) => {
    event?.preventDefault?.();
    event?.stopImmediatePropagation?.();
    event?.stopPropagation?.();
  };
  const getEventElement = (event) => event?.target instanceof Element ? event.target : event?.target?.parentElement;

  const closeDialogElement = (dialog, instant = true) => {
    if (!dialog?.open) return;

    const host = dialog.parentElement;
    if (host && typeof host.close === 'function') {
      host.close(instant);
      return;
    }

    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
  };

  const closeSearchDrawers = () => {
    document.querySelectorAll('#drawer-search-form, #drawer-search-form-full').forEach((dialog) => {
      closeDialogElement(dialog, true);
    });
  };

  const closeToolbarSearchPanels = () => {
    document.querySelectorAll('[data-aios-toolbar-search-panel]').forEach((panel) => {
      panel.classList.remove('is-open');
      panel.hidden = true;
    });
  };

  const closeAiosHeaderMobileSearch = () => {
    const searchToggle = document.querySelector('.aios-mobile-search-toggle[id]');
    if (!searchToggle?.checked) return;

    searchToggle.checked = false;
    searchToggle.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const focusSearchInput = (dialog) => {
    window.requestAnimationFrame(() => {
      dialog?.querySelector('[data-input-search], input[type="search"]')?.focus({ preventScroll: true });
    });
  };

  const openToolbarSearchPanel = () => {
    const panel = document.querySelector('[data-aios-toolbar-search-panel]');
    if (!panel) return false;

    const menuToggle = document.querySelector('.aios-mobile-menu-toggle[id]');
    if (menuToggle?.checked) {
      menuToggle.checked = false;
      menuToggle.dispatchEvent(new Event('change', { bubbles: true }));
    }

    closeDialogElement(getCartDrawer(), true);
    closeSearchDrawers();
    closeAiosHeaderMobileSearch();
    panel.hidden = false;
    window.requestAnimationFrame(() => {
      panel.classList.add('is-open');
      focusSearchInput(panel);
    });
    return true;
  };

  const openAiosHeaderMobileSearch = () => {
    const searchToggle = document.querySelector('.aios-mobile-search-toggle[id]');
    const searchPanel = document.querySelector('.aios-site-header__mobile-search-panel');
    if (!searchToggle || !searchPanel) return false;

    const menuToggle = document.querySelector('.aios-mobile-menu-toggle[id]');
    if (menuToggle?.checked) {
      menuToggle.checked = false;
      menuToggle.dispatchEvent(new Event('change', { bubbles: true }));
    }

    closeDialogElement(getCartDrawer(), true);
    closeSearchDrawers();
    closeToolbarSearchPanels();
    searchToggle.checked = true;
    searchToggle.dispatchEvent(new Event('change', { bubbles: true }));
    focusSearchInput(searchPanel);
    return true;
  };

  const openSearchDrawerById = (targetId, opener = null) => {
    const dialog = document.getElementById(targetId);
    if (!dialog || dialog.tagName !== 'DIALOG') return false;
    if (opener) dialog.btnOpening = opener;

    if (dialog.open) {
      focusSearchInput(dialog);
      return true;
    }

    closeDialogElement(getCartDrawer(), true);
    document.querySelectorAll('#drawer-search-form, #drawer-search-form-full').forEach((searchDialog) => {
      if (searchDialog !== dialog) closeDialogElement(searchDialog, true);
    });

    const host = dialog.parentElement;
    if (host && typeof host.open === 'function') {
      host.open();
    } else if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }

    focusSearchInput(dialog);
    return true;
  };

  const syncCartHostState = (currentHost, nextHost) => {
    if (!currentHost || !nextHost) return;
    ['section-id', 'data-count', 'data-total-price'].forEach((attribute) => {
      const nextValue = nextHost.getAttribute(attribute);
      if (nextValue === null) currentHost.removeAttribute(attribute);
      else currentHost.setAttribute(attribute, nextValue);
    });
  };

  const syncCartDrawerState = (currentDrawer, nextDrawer) => {
    if (!currentDrawer || !nextDrawer) return;
    currentDrawer.className = nextDrawer.className;
    ['pos', 'color-scheme'].forEach((attribute) => {
      const nextValue = nextDrawer.getAttribute(attribute);
      if (nextValue === null) currentDrawer.removeAttribute(attribute);
      else currentDrawer.setAttribute(attribute, nextValue);
    });
  };

  const refreshOpenCartDrawerContent = (currentHost, nextHost) => {
    const currentDrawer = currentHost?.querySelector(cartDrawerSelector);
    const nextDrawer = nextHost?.querySelector(cartDrawerSelector);
    if (!currentDrawer?.open || !nextDrawer) return false;

    const currentContent = getDrawerContent(currentDrawer);
    const nextContent = getDrawerContent(nextDrawer);
    if (!currentContent || !nextContent) return false;

    syncCartHostState(currentHost, nextHost);
    syncCartDrawerState(currentDrawer, nextDrawer);
    currentContent.replaceWith(nextContent);
    return true;
  };

  const openCartDrawer = () => {
    const drawer = getCartDrawer();
    if (!drawer) return false;
    if (drawer.open) return true;
    closeToolbarSearchPanels();
    closeAiosHeaderMobileSearch();
    closeSearchDrawers();

    const drawerHost = drawer.closest('hdt-cart-drawer');

    if (drawerHost && typeof drawerHost.open === 'function') {
      drawerHost.open();
      return true;
    }

    if (typeof drawer.showModal === 'function' && !drawer.open) {
      drawer.showModal();
      return true;
    }

    drawer.setAttribute('open', '');
    return true;
  };

  const applyCartCount = (cart) => {
    if (cart?.item_count == null) return false;
    const count = Number(cart.item_count);
    if (!Number.isFinite(count) || count < 0) return false;
    const selector = 'hdt-cart-count, .aios-header-count, [data-cart-count], .cart-count, .hdt-cart-count, .jsccount, .aios-mobile-toolbar__item--cart .aios-mobile-toolbar__count';
    document.querySelectorAll(selector).forEach((badge) => {
      badge.textContent = String(count);
      badge.classList.toggle('is-empty', count === 0);
    });
    const host = getCartDrawer()?.closest('hdt-cart-drawer');
    if (host) {
      host.setAttribute('data-count', count);
      if (cart.total_price != null) host.setAttribute('data-total-price', cart.total_price);
    }
    return true;
  };

  const updateCartCount = async () => {
    const requestId = ++cartCountRequestId;
    try {
      const response = await fetch(`${getRootUrl()}cart.js`, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return null;
      const cart = await response.json();
      if (requestId !== cartCountRequestId) return null;
      if (applyCartCount(cart)) document.dispatchEvent(new CustomEvent('cart:count:update', { detail: {
        item_count: cart.item_count, total_price: cart.total_price
      } }));
      return cart;
    } catch (error) {
      console.warn('AIOS cart count update failed', error);
      return null;
    }
  };

  const refreshCartDrawer = async ({ keepOpen = false } = {}) => {
    const requestId = ++cartDrawerRefreshId;
    const root = getRootUrl();
    const separator = root.includes('?') ? '&' : '?';
    const response = await fetch(`${root}${separator}section_id=${encodeURIComponent(getCartSectionId())}`, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' }
    });

    if (!response.ok) return false;

    const html = await response.text();
    if (!html || requestId !== cartDrawerRefreshId) return false;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const nextHost = parsed.querySelector('hdt-cart-drawer');
    const currentHost = document.querySelector('hdt-cart-drawer');

    if (nextHost && currentHost) {
      if (keepOpen && refreshOpenCartDrawerContent(currentHost, nextHost)) {
        return true;
      }
      currentHost.replaceWith(nextHost);
      if (keepOpen) window.requestAnimationFrame(openCartDrawer);
      return true;
    }

    const nextDrawer = parsed.querySelector(cartDrawerSelector);
    const currentDrawer = getCartDrawer();
    if (nextDrawer && currentDrawer) {
      if (keepOpen && currentDrawer.open) {
        const currentContent = getDrawerContent(currentDrawer);
        const nextContent = getDrawerContent(nextDrawer);
        if (currentContent && nextContent) {
          syncCartDrawerState(currentDrawer, nextDrawer);
          currentContent.replaceWith(nextContent);
          return true;
        }
      }
      currentDrawer.replaceWith(nextDrawer);
      if (keepOpen) window.requestAnimationFrame(openCartDrawer);
      return true;
    }

    return false;
  };

  const refreshAndOpenCartDrawer = async () => {
    await Promise.all([
      refreshCartDrawer({ keepOpen: true }),
      updateCartCount()
    ]);
    openCartDrawer();
  };

  const escapeHtml = (value) => String(value ?? '').replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    "'": '&#039;',
    '"': '&quot;'
  })[character]);

  const formatMoney = (cents) => {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      const moneyFormat = window.themeHDN?.money_format || window.Shopify.money_format || 'Rs.{{amount_no_decimals}}';
      return window.Shopify.formatMoney(Number(cents || 0), moneyFormat);
    }

    const amount = (Number(cents || 0) / 100).toLocaleString('en-PK', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
    return `Rs.${amount}`;
  };

  const setButtonLoading = (button, loading) => {
    if (!button) return;
    button.classList.toggle('is-loading', loading);
    if (loading) {
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('disabled', '');
    } else {
      button.removeAttribute('aria-busy');
      if (button.dataset.aiosKeepDisabled !== 'true') button.removeAttribute('disabled');
    }
  };

  const showCartError = (message, form) => {
    const productRoot = form?.closest('[data-aios-product-main]');
    const errorBox = productRoot?.querySelector('[data-aios-error]');
    if (errorBox) {
      errorBox.textContent = message;
      errorBox.hidden = false;
      return;
    }
    window.alert(message);
  };

  const addFormToCart = async (form, submitButton) => {
    const button = submitButton || form?.querySelector('[type="submit"]');
    if (!form || button?.classList.contains('is-loading')) return false;
    if (typeof form.aiosValidateVariantSelection === 'function' && !form.aiosValidateVariantSelection()) return false;

    setButtonLoading(button, true);

    try {
      const response = await fetch(form.action || getCartAddUrl(), {
        method: 'POST',
        body: new FormData(form),
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        let message = 'Unable to add this product to cart.';
        try {
          const data = await response.json();
          message = data.description || data.message || message;
        } catch (error) {}
        throw new Error(message);
      }

      await refreshAndOpenCartDrawer();
      return true;
    } catch (error) {
      showCartError(error.message || 'Unable to add this product to cart.', form);
      return false;
    } finally {
      setButtonLoading(button, false);
    }
  };

  const addVariantToCart = async (variantId, button, quantity = 1) => {
    if (!variantId || button?.classList.contains('is-loading')) return false;

    setButtonLoading(button, true);

    try {
      const response = await fetch(getCartAddUrl(), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ id: Number(variantId), quantity: Number(quantity || 1) })
      });

      if (!response.ok) {
        let message = 'Unable to add this product to cart.';
        try {
          const data = await response.json();
          message = data.description || data.message || message;
        } catch (error) {}
        throw new Error(message);
      }

      closeVariantModal({ restoreFocus: false });
      await refreshAndOpenCartDrawer();
      return true;
    } catch (error) {
      const notice = variantModalContent?.querySelector('[data-aios-global-variant-notice]');
      if (notice) {
        notice.hidden = false;
        notice.textContent = error.message || 'Unable to add this product to cart.';
      } else {
        window.alert(error.message || 'Unable to add this product to cart.');
      }
      return false;
    } finally {
      setButtonLoading(button, false);
    }
  };

  const ensureVariantModal = () => {
    if (variantModal?.isConnected) return variantModal;

    const style = document.createElement('style');
    style.dataset.aiosGlobalVariantStyle = '';
    style.textContent = `
      html.aios-global-variant-modal-open { overflow: hidden; }
      .aios-global-variant-modal[hidden] { display: none !important; }
      .aios-global-variant-modal { position: fixed; inset: 0; z-index: 2147483000; display: grid; place-items: center; padding: 18px; background: rgba(12, 27, 39, .48); }
      .aios-global-variant-modal__dialog { position: relative; width: min(430px, 100%); max-height: min(82vh, 720px); overflow: auto; padding: 18px; color: #1b3044; background: #fff; border-radius: 14px; box-shadow: 0 20px 60px rgba(12, 27, 39, .22); }
      .aios-global-variant-modal__close { position: absolute; top: 10px; right: 10px; z-index: 2; display: grid; width: 30px; height: 30px; place-items: center; padding: 0; color: #1b3044; background: #fff; border: 1px solid #dbe5e7; border-radius: 50%; cursor: pointer; }
      .aios-global-variant-modal__close svg { width: 14px; height: 14px; }
      .aios-global-variant-modal__summary { display: grid; grid-template-columns: 68px minmax(0, 1fr); gap: 12px; align-items: center; padding-right: 34px; }
      .aios-global-variant-modal__image { display: block; width: 68px; height: 68px; object-fit: contain; background: #f6f8f9; border-radius: 10px; }
      .aios-global-variant-modal__title { margin: 0; font: inherit; font-size: 16px; font-weight: 500; line-height: 1.25; }
      .aios-global-variant-modal__price { margin: 5px 0 0; color: #105a5b; font-size: 14px; font-weight: 600; line-height: 1.2; }
      .aios-global-variant-modal__price s { margin-left: 5px; color: #7b8792; font-size: .86em; font-weight: 400; }
      .aios-global-variant-modal__options { display: grid; gap: 14px; margin-top: 18px; }
      .aios-global-variant-modal__label { margin: 0 0 7px; font-size: 13px; font-weight: 500; line-height: 1.2; }
      .aios-global-variant-modal__values { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
      .aios-global-variant-modal__option { min-width: 0; min-height: 40px; padding: 7px 9px; color: #1b3044; background: #fff; border: 1px solid #d7e1e5; border-radius: 8px; font: inherit; font-size: 12px; font-weight: 400; line-height: 1.2; cursor: pointer; }
      .aios-global-variant-modal__option:hover, .aios-global-variant-modal__option.is-selected { color: #105a5b; border-color: #105a5b; background: #eef8f6; }
      .aios-global-variant-modal__option:disabled { opacity: .42; cursor: not-allowed; }
      .aios-global-variant-modal__notice { margin: 10px 0 0; color: #bf3030; font-size: 11px; line-height: 1.35; }
      .aios-global-variant-modal__notice[hidden] { display: none; }
      .aios-global-variant-modal__add { display: inline-flex; width: 100%; min-height: 44px; margin-top: 16px; align-items: center; justify-content: center; gap: 7px; padding: 8px 14px; color: #fff; background: #105a5b; border: 0; border-radius: 9px; font: inherit; font-size: 14px; font-weight: 600; cursor: pointer; }
      .aios-global-variant-modal__add svg { width: 16px; height: 16px; }
      .aios-global-variant-modal__add:disabled { opacity: .45; cursor: not-allowed; }
      .aios-global-variant-modal__add.is-loading { opacity: .7; }
      @media (max-width: 767px) {
        .aios-global-variant-modal { align-items: end; padding: 10px; }
        .aios-global-variant-modal__dialog { width: 100%; max-height: 86vh; padding: 15px; border-radius: 13px 13px 8px 8px; }
        .aios-global-variant-modal__summary { grid-template-columns: 58px minmax(0, 1fr); gap: 10px; }
        .aios-global-variant-modal__image { width: 58px; height: 58px; }
        .aios-global-variant-modal__title { font-size: 14px; }
        .aios-global-variant-modal__price { font-size: 13px; }
        .aios-global-variant-modal__options { gap: 12px; margin-top: 14px; }
        .aios-global-variant-modal__label { font-size: 12px; }
        .aios-global-variant-modal__option { min-height: 36px; font-size: 11px; }
        .aios-global-variant-modal__add { min-height: 40px; margin-top: 13px; font-size: 13px; }
      }
    `;
    document.head.appendChild(style);

    variantModal = document.createElement('div');
    variantModal.className = 'aios-global-variant-modal';
    variantModal.hidden = true;
    variantModal.setAttribute('role', 'dialog');
    variantModal.setAttribute('aria-modal', 'true');
    variantModal.setAttribute('aria-label', 'Choose product options');
    variantModal.innerHTML = `
      <div class="aios-global-variant-modal__dialog" role="document">
        <button class="aios-global-variant-modal__close" type="button" data-aios-global-variant-close aria-label="Close option selector">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"/></svg>
        </button>
        <div data-aios-global-variant-content></div>
      </div>
    `;
    document.body.appendChild(variantModal);
    variantModalContent = variantModal.querySelector('[data-aios-global-variant-content]');

    variantModal.addEventListener('click', (event) => {
      event.stopPropagation();
      if (event.target === variantModal || event.target.closest('[data-aios-global-variant-close]')) {
        event.preventDefault();
        closeVariantModal();
      }
    });

    return variantModal;
  };

  function closeVariantModal({ restoreFocus = true } = {}) {
    if (!variantModal) return;
    variantModal.hidden = true;
    document.documentElement.classList.remove('aios-global-variant-modal-open');
    if (variantModalContent) variantModalContent.innerHTML = '';
    if (restoreFocus && variantModalTrigger?.focus) variantModalTrigger.focus({ preventScroll: true });
    variantModalTrigger = null;
  }

  const normalizeProductImage = (image) => {
    if (!image) return '';
    if (typeof image === 'string') return image;
    return image.src || image.url || '';
  };

  const openVariantModal = (product, trigger) => {
    if (!product) return;
    ensureVariantModal();

    const variants = Array.isArray(product.variants) ? product.variants : [];
    const availableVariants = variants.filter((variant) => variant.available);
    const rawOptions = Array.isArray(product.options) ? product.options : [];
    const optionNames = rawOptions.map((option, index) => {
      if (typeof option === 'string') return option;
      return option?.name || `Option ${index + 1}`;
    });
    const actualOptionNames = optionNames.length ? optionNames : ['Options'];
    const selectedOptions = actualOptionNames.map(() => '');
    const fallbackImage = normalizeProductImage(product.featured_image || product.featured_media);

    const optionGroups = actualOptionNames.map((optionName, optionIndex) => {
      const values = Array.from(new Set(availableVariants.map((variant) => variant.options?.[optionIndex]).filter(Boolean)));
      return `
        <div class="aios-global-variant-modal__group" data-aios-global-variant-group="${optionIndex}">
          <p class="aios-global-variant-modal__label">Choose ${escapeHtml(optionName)}</p>
          <div class="aios-global-variant-modal__values">
            ${values.map((value) => {
              return `
                <button
                  class="aios-global-variant-modal__option"
                  type="button"
                  data-aios-global-option-index="${optionIndex}"
                  data-aios-global-option-value="${escapeHtml(value)}"
                >${escapeHtml(value)}</button>
              `;
            }).join('')}
          </div>
        </div>
      `;
    }).join('');

    variantModalContent.innerHTML = `
      <div class="aios-global-variant-modal__summary">
        ${fallbackImage ? `<img class="aios-global-variant-modal__image" src="${escapeHtml(fallbackImage)}" alt="${escapeHtml(product.title)}" loading="lazy">` : ''}
        <div>
          <h3 class="aios-global-variant-modal__title">${escapeHtml(product.title)}</h3>
          <p class="aios-global-variant-modal__price" data-aios-global-variant-price>Select options</p>
        </div>
      </div>
      <div class="aios-global-variant-modal__options">${optionGroups}</div>
      <p class="aios-global-variant-modal__notice" data-aios-global-variant-notice hidden>Please select all required options.</p>
      <button class="aios-global-variant-modal__add" type="button" data-aios-global-variant-add disabled>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 6h2l2 10h8l2-7H8"/><circle cx="10" cy="20" r="1.6"/><circle cx="17" cy="20" r="1.6"/></svg>
        <span>Add to Cart</span>
      </button>
    `;

    const optionButtons = Array.from(variantModalContent.querySelectorAll('[data-aios-global-option-index]'));
    const addButton = variantModalContent.querySelector('[data-aios-global-variant-add]');
    const priceElement = variantModalContent.querySelector('[data-aios-global-variant-price]');
    const notice = variantModalContent.querySelector('[data-aios-global-variant-notice]');
    const imageElement = variantModalContent.querySelector('.aios-global-variant-modal__image');

    const getMatchedVariant = () => {
      if (selectedOptions.some((value) => !value)) return null;
      return availableVariants.find((variant) => selectedOptions.every((value, index) => variant.options?.[index] === value));
    };

    const updateModalState = () => {
      const matchedVariant = getMatchedVariant();
      const complete = selectedOptions.every(Boolean);
      const available = Boolean(matchedVariant?.available);

      optionButtons.forEach((button) => {
        const optionIndex = Number(button.dataset.aiosGlobalOptionIndex);
        const optionValue = button.dataset.aiosGlobalOptionValue;
        const canPurchaseCombination = availableVariants.some((variant) => {
          if (variant.options?.[optionIndex] !== optionValue) return false;

          return selectedOptions.slice(0, optionIndex).every((selectedValue, earlierIndex) => {
            return !selectedValue || variant.options?.[earlierIndex] === selectedValue;
          });
        });

        button.hidden = !canPurchaseCombination;
        button.disabled = !canPurchaseCombination;
        button.classList.toggle('is-selected', selectedOptions[optionIndex] === button.dataset.aiosGlobalOptionValue);
      });

      if (matchedVariant && priceElement) {
        const comparePrice = matchedVariant.compare_at_price && matchedVariant.compare_at_price > matchedVariant.price
          ? `<s>${formatMoney(matchedVariant.compare_at_price)}</s>`
          : '';
        priceElement.innerHTML = `${formatMoney(matchedVariant.price)}${comparePrice}`;
      } else if (priceElement) {
        priceElement.textContent = 'Select options';
      }

      const matchedImage = normalizeProductImage(matchedVariant?.featured_image || matchedVariant?.featured_media);
      if (matchedImage && imageElement) imageElement.src = matchedImage;

      if (addButton) {
        addButton.disabled = !available;
        addButton.dataset.variantId = available ? matchedVariant.id : '';
        addButton.querySelector('span').textContent = complete && !available ? 'Sold Out' : 'Add to Cart';
      }

      if (notice) {
        notice.hidden = !complete || available;
        if (complete && !available) notice.textContent = 'This combination is currently unavailable.';
      }
    };

    optionButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const optionIndex = Number(button.dataset.aiosGlobalOptionIndex);
        selectedOptions[optionIndex] = button.dataset.aiosGlobalOptionValue;

        for (let laterIndex = optionIndex + 1; laterIndex < selectedOptions.length; laterIndex += 1) {
          selectedOptions[laterIndex] = '';
        }

        updateModalState();
      });
    });

    addButton?.addEventListener('click', (event) => {
      stopEvent(event);
      if (!addButton.dataset.variantId) {
        if (notice) {
          notice.hidden = false;
          notice.textContent = 'Please select all required options.';
        }
        return;
      }
      addVariantToCart(addButton.dataset.variantId, addButton);
    });

    variantModalTrigger = trigger || null;
    updateModalState();
    variantModal.hidden = false;
    document.documentElement.classList.add('aios-global-variant-modal-open');
    variantModal.querySelector('[data-aios-global-variant-close]')?.focus({ preventScroll: true });
  };

  const getProductJsonUrl = (href) => {
    const url = new URL(href, window.location.href);
    const path = url.pathname.replace(/\.js$/i, '').replace(/\/$/, '');
    return `${url.origin}${path}.js`;
  };

  const loadProductAndOpenModal = async (link) => {
    if (!link || link.classList.contains('is-loading')) return;

    link.classList.add('is-loading');
    link.setAttribute('aria-busy', 'true');
    productRequestController?.abort();
    productRequestController = new AbortController();

    try {
      const response = await fetch(getProductJsonUrl(link.href), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: productRequestController.signal
      });
      if (!response.ok) throw new Error('Unable to load product options.');

      const product = await response.json();
      openVariantModal(product, link);
    } catch (error) {
      if (error.name !== 'AbortError') window.alert(error.message || 'Unable to load product options.');
    } finally {
      link.classList.remove('is-loading');
      link.removeAttribute('aria-busy');
    }
  };

  const handleCartTriggerClick = (event) => {
    const eventTarget = getEventElement(event);
    const trigger = eventTarget?.closest('[aria-controls="CartDrawer"], [data-aios-cart-open]');
    if (!trigger) return;
    if (trigger.closest(cartDrawerSelector)) return;
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    if (!getCartDrawer()) return;

    stopEvent(event);
    openCartDrawer();
  };

  const handleVariantQuickAddClick = (event) => {
    const eventTarget = getEventElement(event);
    const link = eventTarget?.closest(variantQuickAddSelector);
    if (!link) return;
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    stopEvent(event);
    loadProductAndOpenModal(link);
  };

  const handleToolbarSearchClick = (event) => {
    const eventTarget = getEventElement(event);
    const searchLink = eventTarget?.closest(toolbarSearchSelector);
    if (!searchLink || !searchLink.closest('.aios-mobile-toolbar')) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const targetId = searchLink.getAttribute('aria-controls');
    if (!targetId || !targetId.startsWith('drawer-search-form')) return;

    stopEvent(event);
    if (openToolbarSearchPanel()) return;
    if (openAiosHeaderMobileSearch()) return;
    if (!document.getElementById(targetId)) return;
    openSearchDrawerById(targetId, searchLink);
  };

  const handleAjaxCartSubmit = async (event) => {
    const eventTarget = getEventElement(event);
    const form = eventTarget?.closest(ajaxFormSelector);
    if (!form) return;
    if (event.defaultPrevented) return;
    if (!form.action || !form.action.includes('/cart/add')) return;

    stopEvent(event);
    await addFormToCart(form, event.submitter || form.querySelector('[type="submit"]'));
  };

  const handleCartRemoveClick = async (event) => {
    const eventTarget = getEventElement(event);
    const removeButton = eventTarget?.closest('.aios-cart-item__remove');
    if (!removeButton || !removeButton.closest(cartDrawerSelector)) return;
    if (!getCartDrawer()) return;

    stopEvent(event);
    if (removeButton.getAttribute('aria-busy') === 'true') return;

    const cartItem = removeButton.closest('.aios-cart-item');
    const itemKey = removeButton.dataset.aiosCartItemKey || cartItem?.dataset.aiosCartItemKey || '';

    let line = removeButton.dataset.aiosCartLine || cartItem?.dataset.aiosCartLine || null;
    try {
      const url = new URL(removeButton.href, window.location.origin);
      line = line || url.searchParams.get('line');
    } catch (error) {}

    const changeParams = new URLSearchParams({ quantity: '0' });
    if (itemKey) changeParams.set('id', itemKey);
    else if (line) changeParams.set('line', line);
    else return;

    removeButton.setAttribute('aria-busy', 'true');

    try {
      const changeUrl = new URL(getCartChangeUrl(), window.location.origin);
      changeUrl.pathname = changeUrl.pathname.replace(/\/cart\/change\/?$/i, '/cart/change.js');

      const response = await fetch(changeUrl.toString(), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: changeParams
      });
      if (!response.ok) throw new Error('Unable to remove this product from cart.');

      await refreshAndOpenCartDrawer();
    } catch (error) {
      window.alert(error.message || 'Unable to remove this product from cart.');
    } finally {
      removeButton.removeAttribute('aria-busy');
    }
  };

  const handleExternalCartUpdate = async () => {
    if (externalCartRefreshRunning) return;
    externalCartRefreshRunning = true;
    try {
      await refreshAndOpenCartDrawer();
    } finally {
      externalCartRefreshRunning = false;
    }
  };

  window.addEventListener('click', handleCartRemoveClick, true);
  window.addEventListener('click', handleToolbarSearchClick, true);
  window.addEventListener('touchend', handleToolbarSearchClick, { capture: true, passive: false });
  window.addEventListener('click', handleCartTriggerClick, true);
  window.addEventListener('click', handleVariantQuickAddClick, true);
  window.addEventListener('submit', handleAjaxCartSubmit, true);
  document.addEventListener('aios:cart:updated', handleExternalCartUpdate);
  document.addEventListener('cart:count:update', (event) => {
    if (event.detail?.item_count == null) return;
    cartCountRequestId += 1;
    applyCartCount(event.detail);
  });
  document.addEventListener('cart:update', (event) => {
    const cart = event.detail?.cartData;
    if (cart?.item_count != null) {
      cartCountRequestId += 1;
      applyCartCount(cart);
    } else {
      updateCartCount();
    }
  });
  document.addEventListener('shopify:section:load', updateCartCount);
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) updateCartCount();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', updateCartCount, { once: true });
  else updateCartCount();
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && variantModal && !variantModal.hidden) closeVariantModal();
  });

  window.AIOSCartDrawer = {
    open: openCartDrawer,
    refresh: refreshCartDrawer,
    refreshAndOpen: refreshAndOpenCartDrawer,
    updateCount: updateCartCount,
    addForm: addFormToCart,
    addVariant: addVariantToCart,
    openVariantPicker: openVariantModal
  };
})();
